import { MouseEvent, useMemo, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { FormProvider, useFormContext, useWatch } from 'react-hook-form'

import { LoadingButton } from '@mui/lab'
import { Button, Grid, Menu, MenuItem, Stack, Tooltip } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import SelectInputController from '@/components/ui/inputs/SelectInputController'
import TextInput from '@/components/ui/inputs/TextInput'
import TextInputController from '@/components/ui/inputs/TextInputController'

import { useAppSelector } from '@/redux/rootReducer'
import { selectPracticeLocations } from '@/redux/reducers/dmp-connect.reducer'

import { useTlsiConnectorForm, TlsiConnectorFormData } from '../model/useTlsiConnectorForm'
import { usePracticeLocationDetails } from '../model/usePracticeLocationDetails'
import { usePracticeSetting } from '../model/usePracticeSetting'

export function TlsiConnectorConfig() {
  useLingui()

  const { rhForm, isDirty, isValid, isCreatingInsiConnector, onSubmit, resetCard, selectPracticeLocation } = useTlsiConnectorForm()

  return (
    <FormProvider {...rhForm}>
      <form onSubmit={rhForm.handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <SelectPracticeLocation onSelect={selectPracticeLocation} />
            <PracticeLocationDetails />
          </Stack>
          <PracticeSetting />
          <BillingNumber />
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack direction="row" spacing={2}>
              <LoadingButton disabled={!isValid || !isDirty} loading={isCreatingInsiConnector} type="submit" variant="contained">
                <Trans>Save</Trans>
              </LoadingButton>
              <Button onClick={() => rhForm.reset()} variant="neutral">
                <Trans>Reset</Trans>
              </Button>
            </Stack>
            <Button onClick={resetCard} variant="neutral">
              <Trans>Change card</Trans>
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  )
}

function BillingNumber() {
  useLingui()
  const rhForm = useFormContext<TlsiConnectorFormData>()
  return (
    <TextInputController
      control={rhForm.control}
      label={
        <div className="flex items-center space-x-2">
          <Trans>Billing number or FINESS</Trans>
          <Tooltip
            placement="top"
            title={
              <span>
                <Trans id="Information nécessaire pour l'accès aux téléservices (INSi, ALDi,...).<0/>Veuillez saisir le FINESS géographique de l'établissement de santé si vous utilisez le logiciel en environnement hospitalier.">
                  Information required to access online health services (INSi, ALDi, etc.).
                  <br />
                  Enter the healthcare establishment's geographic FINESS number when using the software in a hospital environment.
                </Trans>
              </span>
            }
          >
            <InfoOutlinedIcon fontSize="small" />
          </Tooltip>
        </div>
      }
      name="billingNumber"
    />
  )
}

function PracticeLocationDetails() {
  useLingui()
  const practiceLocations = useAppSelector(selectPracticeLocations)

  const rhForm = useFormContext<TlsiConnectorFormData>()
  const practiceLocation = useWatch({ control: rhForm.control, name: 'practiceLocation' })

  const selectedPracticeLocation = useMemo(() => practiceLocations?.[practiceLocation], [practiceLocations, practiceLocation])

  const { structureName, structureIdType, structureId, activitySector } = usePracticeLocationDetails(selectedPracticeLocation)

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextInput fullWidth label={t`Name of the structure`} value={structureName} readOnly />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInput fullWidth label={t`Structure identifier type`} value={structureIdType} readOnly />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInput fullWidth label={t`Structure identifier`} name="practiceLocationStructureId" value={structureId} readOnly />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextInput fullWidth label={t`Activity sector`} value={activitySector} readOnly />
        </Grid>
      </Grid>
    </div>
  )
}

function PracticeSetting() {
  const { practiceSettings } = usePracticeSetting()

  const rhForm = useFormContext<TlsiConnectorFormData>()

  return (
    <SelectInputController
      control={rhForm.control}
      fullWidth
      label={t`Please choose your field of practice`}
      name="practiceSetting"
      options={practiceSettings}
    />
  )
}

interface SelectPracticeLocationProps {
  onSelect: (index: number, location: any) => void
}

function SelectPracticeLocation({ onSelect }: SelectPracticeLocationProps) {
  const practiceLocations = useAppSelector(selectPracticeLocations)

  const rhForm = useFormContext<TlsiConnectorFormData>()
  const practiceLocation = useWatch({ control: rhForm.control, name: 'practiceLocation' })
  const selectedPracticeLocation = useMemo(() => practiceLocations?.[practiceLocation], [practiceLocations, practiceLocation])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClickListItem = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  return (
    <div>
      <Button endIcon={<KeyboardArrowDownIcon />} onClick={handleClickListItem}>
        {selectedPracticeLocation?.s_practiceLocationName ?? <Trans>Select a practice setting</Trans>}
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {practiceLocations?.map((location, index) => (
          <MenuItem
            key={location.s_practiceLocationStructureId}
            onClick={() => {
              setAnchorEl(null)
              onSelect(index, location)
            }}
          >
            {location.s_practiceLocationName}
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}
